<p-toast></p-toast>

<div class="locations-page p-fluid" *ngIf="canViewLocations; else noAccess">

    <section class="locations-hero">
        <div class="locations-title">
            <div class="locations-eyebrow">Operations Setup</div>
            <h1>Locations</h1>
            <p>Maintain customer, warehouse, office, and carrier locations used across operations.</p>
        </div>
        <div class="locations-actions">
            <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadLocations()"></p-button>
            <p-button *ngIf="canCreateLocation || canEditLocation" label="Import from Excel" icon="pi pi-upload" severity="secondary"
                [outlined]="true" routerLink="/location/import"></p-button>
            <p-button *ngIf="canEditLocation && canDeleteLocation" label="Manage Duplicates" icon="pi pi-clone" severity="secondary"
                [outlined]="true" routerLink="/location/merge-duplicates"></p-button>
            <p-button label="Export to Excel" icon="pi pi-file-excel" severity="secondary" [outlined]="true"
                [disabled]="isLoading || isReferenceLoading || !filteredLocations.length" (click)="exportToExcel()"></p-button>
            <p-button *ngIf="canCreateLocation" label="Add Location" icon="pi pi-plus" (click)="addLocation()"></p-button>
        </div>
    </section>

    <section class="locations-metrics" aria-label="Location summary">
        <div class="metric-tile">
            <span class="metric-label">Total locations</span>
            <strong>{{ totalRequests }}</strong>
        </div>
        <div class="metric-tile">
            <span class="metric-label">Warehouses</span>
            <strong>{{ warehouseCount }}</strong>
        </div>
        <div class="metric-tile">
            <span class="metric-label">Customers</span>
            <strong>{{ customerCount }}</strong>
        </div>
        <div class="metric-tile">
            <span class="metric-label">Current result</span>
            <strong>{{ filteredLocations.length }}</strong>
        </div>
    </section>

    <section class="locations-search card">
        <div class="section-header">
            <div>
                <h2>Search Locations</h2>
                <p>{{ activeSearchCount }} active filters</p>
            </div>
            <div class="section-actions">
                <p-button label="Clear" icon="pi pi-filter-slash" severity="secondary" [outlined]="true" (click)="clearSearch()"></p-button>
                <p-button label="Search" icon="pi pi-search" (click)="applySearch()"></p-button>
            </div>
        </div>

        <form [formGroup]="searchForm" class="search-grid" (ngSubmit)="applySearch()">
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="locationCodeSearch" pInputText formControlName="code">
                        <label for="locationCodeSearch">Code</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="locationNameSearch" pInputText formControlName="name">
                        <label for="locationNameSearch">Name</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="locationAddressSearch" pInputText formControlName="address">
                        <label for="locationAddressSearch">Address</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="locationCompanySearch" pInputText formControlName="company">
                        <label for="locationCompanySearch">Company</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="locationTypeSearch" [options]="locationTypeOptions" optionLabel="label" optionValue="value" formControlName="type" [showClear]="true" styleClass="w-full"></p-select>
                        <label for="locationTypeSearch">Type</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="locationContactSearch" pInputText formControlName="contact">
                        <label for="locationContactSearch">Contact</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-globe"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="locationCountrySearch" [options]="countryOptions" optionLabel="label" optionValue="value"
                            formControlName="countryId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                        <label for="locationCountrySearch">Country</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building-columns"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="locationCitySearch" [options]="availableCityOptions" optionLabel="label" optionValue="value"
                            formControlName="cityId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                        <label for="locationCitySearch">City</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="locationDistrictSearch" pInputText formControlName="district">
                        <label for="locationDistrictSearch">District</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-shop"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="locationRetailerSearch" [options]="retailerOptions" optionLabel="label" optionValue="value"
                            formControlName="retailerId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                        <label for="locationRetailerSearch">Retailer / Store Chain</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="locationZoneSearch" [options]="zoneOptions" optionLabel="label" optionValue="value"
                            formControlName="locationZoneId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select>
                        <label for="locationZoneSearch">Location Zone</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
        </form>
    </section>

    <section class="locations-table card">
        <div class="section-header table-heading">
            <div>
                <h2>Location Directory</h2>
                <p *ngIf="lastUpdated">Updated {{ lastUpdated | date:'shortTime' }}</p>
            </div>
            <span class="result-chip">{{ filteredLocations.length }} of {{ totalRequests }} records</span>
        </div>

        <p-table [value]="filteredLocations" dataKey="id" [rowHover]="true" [loading]="isLoading" [paginator]="true"
                 [rows]="rows" [(first)]="first" [showCurrentPageReport]="true"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                 [rowsPerPageOptions]="[20, 50, 100]" styleClass="p-datatable-sm p-datatable-gridlines locations-data-table"
                 [tableStyle]="{ 'width': '100%', 'table-layout': 'fixed' }">
            <ng-template pTemplate="header">
                <tr>
                    <th class="location-actions-column">Actions</th>
                    <th class="location-context-column">Location &amp; Context</th>
                    <th class="location-address-column">Address &amp; Geography</th>
                    <th class="location-association-column">Association &amp; Zone</th>
                    <th class="location-access-column">Contact &amp; Access</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-location>
                <tr>
                    <td class="location-actions-column">
                        <span class="location-mobile-label">Actions</span>
                        <div class="row-actions">
                            <button pButton type="button" icon="pi pi-eye" (click)="viewLocation(location)"
                                    class="p-button-rounded p-button-text p-button-info" pTooltip="View location"></button>
                            <button pButton type="button" icon="pi pi-map-marker" (click)="openMap(location)"
                                    class="p-button-rounded p-button-text p-button-help" pTooltip="Open map"></button>
                            <button *ngIf="canEditLocation" pButton type="button" icon="pi pi-pencil" (click)="editLocation(location)"
                                    class="p-button-rounded p-button-text p-button-secondary" pTooltip="Edit"></button>
                            <button *ngIf="canDeleteLocation" pButton type="button" icon="pi pi-trash" (click)="deleteLocation(location)"
                                    class="p-button-rounded p-button-text p-button-danger" pTooltip="Delete"></button>
                        </div>
                    </td>
                    <td>
                        <span class="location-mobile-label">Location &amp; Context</span>
                        <div class="location-identity-cell">
                            <button type="button" class="link-button cell-title" (click)="viewLocation(location)">
                                {{ location.name || '-' }}
                            </button>
                            <div class="cell-meta">
                                <span class="code-pill">{{ locationCode(location) }}</span>
                                <p-tag [value]="location.locationType || 'N/A'" severity="info"></p-tag>
                            </div>
                        </div>
                    </td>
                    <td>
                        <span class="location-mobile-label">Address &amp; Geography</span>
                        <div class="location-detail-stack">
                            <strong class="location-detail-primary">{{ getLocationAddress(location) }}</strong>
                            <span class="location-detail-meta"><i class="pi pi-globe"></i>{{ cityName(location) }}, {{ countryName(location) }}</span>
                            <span class="location-detail-meta"><i class="pi pi-map"></i>District: {{ districtLabel(location) }}</span>
                        </div>
                    </td>
                    <td>
                        <span class="location-mobile-label">Association &amp; Zone</span>
                        <div class="location-detail-stack">
                            <strong class="location-detail-primary">{{ location.company?.name || '-' }}</strong>
                            <span class="location-detail-meta"><i class="pi pi-shop"></i>Retailer: {{ retailerName(location) }}</span>
                            <span class="location-detail-meta"><i class="pi pi-sitemap"></i>Zone: {{ zoneName(location) }}</span>
                        </div>
                    </td>
                    <td>
                        <span class="location-mobile-label">Contact &amp; Access</span>
                        <div class="contact-cell">
                            <p-chip *ngIf="location.defaultContact; else noContact"
                                [label]="location.defaultContact.name || 'Contact'"
                                [image]="location.defaultContact.imageUrl || 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'"
                                styleClass="location-contact-chip"
                                [pTooltip]="location.defaultContact.phoneNumber || location.defaultContact.emailAddress || 'Default contact'">
                            </p-chip>
                            <ng-template #noContact>
                                <span class="muted-text">No contact</span>
                            </ng-template>
                            <small *ngIf="location.contacts?.length || location.merchandisers?.length" class="muted-text">
                                {{ location.contacts?.length || 0 }} contacts, {{ location.merchandisers?.length || 0 }} merchandisers
                            </small>
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="5">
                        <div class="empty-state">
                            <i class="pi pi-search"></i>
                            <strong>No locations found</strong>
                            <span>No matching location records.</span>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </section>
</div>

<ng-template #noAccess>
    <div class="card no-access">
        <i class="pi pi-lock"></i>
        <h2>Locations are restricted</h2>
        <p>You do not have permission to view location records.</p>
    </div>
</ng-template>
